import { m } from 'framer-motion';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';

import { useBoolean } from 'src/hooks/use-boolean';

import Iconify from 'src/components/iconify';
import { varHover } from 'src/components/animate';

import ViewButton from './view-button';
import DownloadButton from './download-button';
import { fileData, fileThumb, fileFormat } from './utils';

// ----------------------------------------------------------------------

export default function FileThumbnail({ onView, file, tooltip, imageView, onDownload, sx, imgSx }) {
  const { name = '', path = '', preview = '' } = fileData(file);
  const openImageDialog = useBoolean();

  const format = fileFormat(path || preview);

  const imageModal = (
    <Dialog open={openImageDialog.value} onClose={openImageDialog.onFalse}>
      <DialogTitle
        sx={{
          pb: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6" sx={{ mb: 1 }}>
            Vista previa
          </Typography>
          <Tooltip title="Cerrar" arrow placement="top">
            <Fab
              component={m.button}
              whileTap="tap"
              whileHover="hover"
              variants={varHover()}
              size="small"
              color="error"
              onClick={openImageDialog.onFalse}
            >
              <Iconify icon="maki:cross" width={18} />
            </Fab>
          </Tooltip>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="center">
          <img src={preview} alt={name} style={{ borderRadius: '10px' }} />
        </Stack>
      </DialogContent>
    </Dialog>
  );

  const renderContent =
    format === 'image' && imageView ? (
      <Box
        component="img"
        src={preview}
        sx={{
          width: 1,
          height: 1,
          flexShrink: 0,
          objectFit: 'cover',
          ...imgSx,
        }}
      />
    ) : (
      <Box
        component="img"
        src={fileThumb(format)}
        sx={{
          width: 50,
          height: 50,
          flexShrink: 0,
          ...sx,
        }}
      />
    );

  if (tooltip) {
    return (
      <>
        <Tooltip title={name}>
          <Stack
            flexShrink={0}
            component="span"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 'fit-content',
              height: 'inherit',
              borderRadius: 2,
            }}
          >
            {renderContent}
            {onDownload && <DownloadButton onDownload={onDownload} />}
            {onView && <ViewButton onView={openImageDialog.onTrue} />}
          </Stack>
        </Tooltip>
        {imageModal}
      </>
    );
  }

  return (
    <>
      {renderContent}
      {onDownload && <DownloadButton onDownload={onDownload} />}
      {onView && <ViewButton onView={openImageDialog.onTrue} />}
      {imageModal}
    </>
  );
}

FileThumbnail.propTypes = {
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  imageView: PropTypes.bool,
  imgSx: PropTypes.object,
  onDownload: PropTypes.func,
  onView: PropTypes.bool,
  sx: PropTypes.object,
  tooltip: PropTypes.bool,
};
